import React, { useEffect, useMemo, useState } from "react";
import { api, inr, formatApiError, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ArrowsClockwise, MicrosoftExcelLogo, FilePdf, Printer, Code,
  FileText, Warning
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { downloadCSV, printReport } from "@/lib/reportExport";

const monthNow = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

function downloadJSON(filename, obj) {
  const json = JSON.stringify(obj, null, 2);
  const blob = new Blob([json], { type: "application/json;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename.endsWith(".json") ? filename : `${filename}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function TabToolbar({ label, onExcel, onJson, testidPrefix }) {
  return (
    <div className="no-print flex items-center justify-between flex-wrap gap-2 mb-3">
      <div className="eyebrow">{label}</div>
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" className="h-8" onClick={onExcel} data-testid={`${testidPrefix}-excel-btn`}>
          <MicrosoftExcelLogo size={14} className="mr-1.5" /> Excel
        </Button>
        {onJson && (
          <Button variant="outline" size="sm" className="h-8" onClick={onJson} data-testid={`${testidPrefix}-json-btn`}>
            <Code size={14} className="mr-1.5" /> GSTN JSON
          </Button>
        )}
        <Button variant="outline" size="sm" className="h-8" onClick={() => printReport("report-print-root")} data-testid={`${testidPrefix}-pdf-btn`}>
          <FilePdf size={14} className="mr-1.5" /> Save PDF
        </Button>
        <Button variant="outline" size="sm" className="h-8" onClick={() => printReport("report-print-root")} data-testid={`${testidPrefix}-print-btn`}>
          <Printer size={14} className="mr-1.5" /> Print
        </Button>
      </div>
    </div>
  );
}

export default function GstReports() {
  const { activeCompanyId, activeStoreId } = useAuth();
  const [month, setMonth] = useState(monthNow());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (!activeCompanyId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/gst/filing?month=${month}`);
      setData(data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeCompanyId, activeStoreId, month]);

  const downloadGstnJson = async (type) => {
    try {
      const { data } = await api.get(`/gst/filing/${type}-json?month=${month}`);
      downloadJSON(`${type}-${month}`, data);
      toast.success(`${type.toUpperCase()} JSON downloaded`);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="page-shell space-y-6" id="report-print-root">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow">Compliance</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">GST Reports</h1>
          <p className="text-sm text-muted-foreground mt-1">GSTR-1, GSTR-3B, HSN, B2B, B2C — ready for portal upload.</p>
        </div>
        <div className="flex items-end gap-3 no-print">
          <div className="space-y-1.5">
            <Label className="eyebrow">Return Period</Label>
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
              className="font-mono h-9 w-44" data-testid="gst-month" />
          </div>
          <Button variant="outline" onClick={load} className="h-9" data-testid="gst-refresh">
            <ArrowsClockwise size={14} className="mr-1.5" /> Refresh
          </Button>
        </div>
      </div>

      {loading && !data ? (
        <div className="text-sm text-muted-foreground">Loading filing data…</div>
      ) : !data ? (
        <div className="text-sm text-muted-foreground">No data</div>
      ) : (
        <>
          {/* Header summary */}
          <div className="card-flat p-6 grid grid-cols-2 md:grid-cols-6 gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Company</div>
              <div className="font-semibold text-sm mt-0.5">{data.company.name || "—"}</div>
              <div className="text-[11px] text-muted-foreground font-mono">{data.company.gstin || "—"}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Period</div>
              <div className="font-semibold text-sm mt-0.5">{data.period.start} — {data.period.end}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Sales</div>
              <div className="font-semibold font-mono text-sm mt-0.5">{inr(data.totals.sales_total)}</div>
              <div className="text-[11px] text-muted-foreground">{data.totals.sales_count} invoice(s)</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Output Tax</div>
              <div className="font-semibold font-mono text-sm mt-0.5">{inr(data.totals.output_cgst + data.totals.output_sgst + data.totals.output_igst)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Input Tax (ITC)</div>
              <div className="font-semibold font-mono text-sm mt-0.5">{inr(data.totals.input_cgst + data.totals.input_sgst + data.totals.input_igst)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Net Payable</div>
              <div className="font-semibold font-mono text-sm mt-0.5" style={{ color: "hsl(var(--warning))" }}>
                {inr(data.totals.net_cgst + data.totals.net_sgst + data.totals.net_igst)}
              </div>
            </div>
          </div>

          <Tabs defaultValue="gstr1">
            <TabsList className="no-print" data-testid="gst-tabs">
              <TabsTrigger value="gstr1" data-testid="gst-tab-gstr1"><FileText size={13} className="mr-1.5" /> GSTR-1</TabsTrigger>
              <TabsTrigger value="gstr3b" data-testid="gst-tab-gstr3b"><FileText size={13} className="mr-1.5" /> GSTR-3B</TabsTrigger>
              <TabsTrigger value="hsn" data-testid="gst-tab-hsn">HSN Summary</TabsTrigger>
              <TabsTrigger value="b2b" data-testid="gst-tab-b2b">B2B</TabsTrigger>
              <TabsTrigger value="b2c" data-testid="gst-tab-b2c">B2C</TabsTrigger>
              <TabsTrigger value="cdnr" data-testid="gst-tab-cdnr">Credit Notes</TabsTrigger>
            </TabsList>

            {/* GSTR-1 Overview */}
            <TabsContent value="gstr1" className="mt-4 space-y-4">
              <TabToolbar
                label="GSTR-1 — Outward Supplies Summary"
                testidPrefix="gstr1"
                onExcel={() => {
                  const rows = [
                    { section: "B2B", count: data.gstr1.b2b.length, taxable: data.gstr1.b2b.reduce((s,r)=>s+r.taxable,0), tax: data.gstr1.b2b.reduce((s,r)=>s+r.cgst+r.sgst+r.igst,0) },
                    { section: "B2C Small (B2CS)", count: data.gstr1.b2cs.length, taxable: data.gstr1.b2cs.reduce((s,r)=>s+r.taxable,0), tax: data.gstr1.b2cs.reduce((s,r)=>s+r.cgst+r.sgst+r.igst,0) },
                    { section: "B2C Large (B2CL)", count: data.gstr1.b2cl.length, taxable: data.gstr1.b2cl.reduce((s,r)=>s+r.taxable,0), tax: data.gstr1.b2cl.reduce((s,r)=>s+r.igst,0) },
                    { section: "Credit Notes (CDNR)", count: data.gstr1.cdnr.length, taxable: data.gstr1.cdnr.reduce((s,r)=>s+r.taxable,0), tax: data.gstr1.cdnr.reduce((s,r)=>s+r.cgst+r.sgst+r.igst,0) },
                  ];
                  downloadCSV(`gstr1-summary-${month}`, [
                    { key: "section", label: "Section" },
                    { key: "count", label: "Count" },
                    { key: "taxable", label: "Taxable Value" },
                    { key: "tax", label: "Tax Amount" },
                  ], rows);
                }}
                onJson={() => downloadGstnJson("gstr1")}
              />
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <SectionCard title="B2B" count={data.gstr1.b2b.length} taxable={data.gstr1.b2b.reduce((s,r)=>s+r.taxable,0)} />
                <SectionCard title="B2CS" count={data.gstr1.b2cs.length} taxable={data.gstr1.b2cs.reduce((s,r)=>s+r.taxable,0)} />
                <SectionCard title="B2CL" count={data.gstr1.b2cl.length} taxable={data.gstr1.b2cl.reduce((s,r)=>s+r.taxable,0)} />
                <SectionCard title="CDNR" count={data.gstr1.cdnr.length} taxable={data.gstr1.cdnr.reduce((s,r)=>s+r.taxable,0)} />
              </div>
              <div className="card-flat p-4 text-[12px] text-muted-foreground">
                <Warning size={14} className="inline mr-1.5" style={{ color: "hsl(var(--warning))" }} />
                Excel = full period CSV · GSTN JSON = portal-ready file for direct upload · PDF/Print = official printout.
              </div>
            </TabsContent>

            {/* GSTR-3B */}
            <TabsContent value="gstr3b" className="mt-4 space-y-4">
              <TabToolbar
                label="GSTR-3B — Return Summary"
                testidPrefix="gstr3b"
                onExcel={() => {
                  const g = data.gstr3b;
                  const rows = [
                    { row: "3.1(a) Outward taxable supplies", ...g.section_3_1_a_outward_taxable },
                    { row: "3.1(b) Zero-rated supplies", ...g.section_3_1_b_outward_zero_rated },
                    { row: "3.1(c) Nil-rated / exempted", ...g.section_3_1_c_outward_nil_rated },
                    { row: "3.1(d) Inward reverse charge", ...g.section_3_1_d_inward_reverse_charge },
                    { row: "3.1(e) Non-GST outward", ...g.section_3_1_e_non_gst_outward },
                    { row: "4(A) ITC Available", taxable: "—", ...g.section_4_a_itc_available },
                    { row: "Net Tax Payable", taxable: "—", ...g.net_tax_payable },
                  ];
                  downloadCSV(`gstr3b-${month}`, [
                    { key: "row", label: "Section" },
                    { key: "taxable", label: "Taxable" },
                    { key: "cgst", label: "CGST" },
                    { key: "sgst", label: "SGST" },
                    { key: "igst", label: "IGST" },
                    { key: "total", label: "Total" },
                  ], rows);
                }}
                onJson={() => downloadGstnJson("gstr3b")}
              />
              <div className="card-flat overflow-hidden">
                <table className="data-table" data-testid="gstr3b-table">
                  <thead>
                    <tr><th>Section</th><th className="num">Taxable</th><th className="num">CGST</th><th className="num">SGST</th><th className="num">IGST</th><th className="num">Total</th></tr>
                  </thead>
                  <tbody>
                    <Gstr3bRow label="3.1(a) Outward taxable supplies (other than zero rated)" row={data.gstr3b.section_3_1_a_outward_taxable} />
                    <Gstr3bRow label="3.1(b) Outward taxable supplies (zero rated)" row={data.gstr3b.section_3_1_b_outward_zero_rated} />
                    <Gstr3bRow label="3.1(c) Other outward supplies (nil rated, exempted)" row={data.gstr3b.section_3_1_c_outward_nil_rated} />
                    <Gstr3bRow label="3.1(d) Inward supplies (liable to reverse charge)" row={data.gstr3b.section_3_1_d_inward_reverse_charge} />
                    <Gstr3bRow label="3.1(e) Non-GST outward supplies" row={data.gstr3b.section_3_1_e_non_gst_outward} />
                    <tr className="bg-secondary/40 font-semibold">
                      <td>4(A) ITC Available</td>
                      <td className="num text-muted-foreground">—</td>
                      <td className="num font-mono">{inr(data.gstr3b.section_4_a_itc_available.cgst)}</td>
                      <td className="num font-mono">{inr(data.gstr3b.section_4_a_itc_available.sgst)}</td>
                      <td className="num font-mono">{inr(data.gstr3b.section_4_a_itc_available.igst)}</td>
                      <td className="num font-mono">{inr(data.gstr3b.section_4_a_itc_available.total)}</td>
                    </tr>
                    <tr className="font-heading font-bold" style={{ background: "hsla(30,92%,55%,0.08)" }}>
                      <td>Net Tax Payable</td>
                      <td className="num text-muted-foreground">—</td>
                      <td className="num font-mono" data-testid="net-cgst">{inr(data.gstr3b.net_tax_payable.cgst)}</td>
                      <td className="num font-mono" data-testid="net-sgst">{inr(data.gstr3b.net_tax_payable.sgst)}</td>
                      <td className="num font-mono" data-testid="net-igst">{inr(data.gstr3b.net_tax_payable.igst)}</td>
                      <td className="num font-mono" data-testid="net-total">{inr(data.gstr3b.net_tax_payable.total)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </TabsContent>

            {/* HSN Summary */}
            <TabsContent value="hsn" className="mt-4 space-y-4">
              <TabToolbar
                label="HSN-wise Summary"
                testidPrefix="gst-hsn"
                onExcel={() => downloadCSV(`gst-hsn-${month}`, [
                  { key: "hsn", label: "HSN / SAC" },
                  { key: "description", label: "Description" },
                  { key: "uqc", label: "UQC (Unit)" },
                  { key: "quantity", label: "Quantity" },
                  { key: "rate", label: "Rate %" },
                  { key: "taxable", label: "Taxable Value" },
                  { key: "cgst", label: "CGST" },
                  { key: "sgst", label: "SGST" },
                  { key: "igst", label: "IGST" },
                  { key: "total", label: "Total" },
                ], data.gstr1.hsn)}
              />
              <div className="card-flat overflow-hidden">
                <table className="data-table" data-testid="hsn-table">
                  <thead>
                    <tr><th>HSN</th><th>Description</th><th>UQC</th><th className="num">Qty</th><th className="num">Rate %</th><th className="num">Taxable</th><th className="num">CGST</th><th className="num">SGST</th><th className="num">IGST</th><th className="num">Total</th></tr>
                  </thead>
                  <tbody>
                    {data.gstr1.hsn.length === 0 ? (
                      <tr><td colSpan={10} className="text-center text-muted-foreground py-6">No HSN data for this period.</td></tr>
                    ) : data.gstr1.hsn.map((r, i) => (
                      <tr key={`hsn-${r.hsn || 'x'}-${r.tax_rate ?? 'x'}-${i}`}>
                        <td className="font-mono">{r.hsn}</td>
                        <td>{r.description}</td>
                        <td>{r.uqc}</td>
                        <td className="num font-mono">{r.quantity.toFixed(2)}</td>
                        <td className="num font-mono">{r.rate}%</td>
                        <td className="num font-mono">{inr(r.taxable)}</td>
                        <td className="num font-mono">{inr(r.cgst)}</td>
                        <td className="num font-mono">{inr(r.sgst)}</td>
                        <td className="num font-mono">{inr(r.igst)}</td>
                        <td className="num font-mono font-semibold">{inr(r.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </TabsContent>

            {/* B2B */}
            <TabsContent value="b2b" className="mt-4 space-y-4">
              <TabToolbar
                label="B2B — Registered Customers"
                testidPrefix="gst-b2b"
                onExcel={() => downloadCSV(`gst-b2b-${month}`, [
                  { key: "gstin", label: "Customer GSTIN" },
                  { key: "party_name", label: "Customer" },
                  { key: "place_of_supply", label: "Place of Supply" },
                  { key: "taxable", label: "Taxable Value" },
                  { key: "cgst", label: "CGST" },
                  { key: "sgst", label: "SGST" },
                  { key: "igst", label: "IGST" },
                  { key: "total", label: "Total" },
                ], data.gstr1.b2b)}
              />
              <div className="card-flat overflow-hidden">
                <table className="data-table" data-testid="b2b-table">
                  <thead>
                    <tr><th>Customer GSTIN</th><th>Customer</th><th>Place of Supply</th><th className="num">Taxable</th><th className="num">CGST</th><th className="num">SGST</th><th className="num">IGST</th><th className="num">Total</th></tr>
                  </thead>
                  <tbody>
                    {data.gstr1.b2b.length === 0 ? (
                      <tr><td colSpan={8} className="text-center text-muted-foreground py-6">No B2B invoices for this period.</td></tr>
                    ) : data.gstr1.b2b.map((r) => (
                      <tr key={r.gstin}>
                        <td className="font-mono text-[12.5px]">{r.gstin}</td>
                        <td>{r.party_name}</td>
                        <td className="font-mono">{r.place_of_supply}</td>
                        <td className="num font-mono">{inr(r.taxable)}</td>
                        <td className="num font-mono">{inr(r.cgst)}</td>
                        <td className="num font-mono">{inr(r.sgst)}</td>
                        <td className="num font-mono">{inr(r.igst)}</td>
                        <td className="num font-mono font-semibold">{inr(r.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </TabsContent>

            {/* B2C */}
            <TabsContent value="b2c" className="mt-4 space-y-4">
              <TabToolbar
                label="B2C — Unregistered Customers (B2CS + B2CL)"
                testidPrefix="gst-b2c"
                onExcel={() => downloadCSV(`gst-b2c-${month}`, [
                  { key: "kind", label: "Kind" },
                  { key: "place_of_supply", label: "Place of Supply" },
                  { key: "rate", label: "Rate %" },
                  { key: "type", label: "Inter/Intra" },
                  { key: "invoice", label: "Invoice #" },
                  { key: "customer_name", label: "Customer" },
                  { key: "taxable", label: "Taxable" },
                  { key: "cgst", label: "CGST" },
                  { key: "sgst", label: "SGST" },
                  { key: "igst", label: "IGST" },
                  { key: "total", label: "Total" },
                ], [
                  ...data.gstr1.b2cs.map(r => ({ kind: "B2CS (small)", ...r })),
                  ...data.gstr1.b2cl.map(r => ({ kind: "B2CL (>₹2.5L)", place_of_supply: r.place_of_supply, invoice: r.number, customer_name: r.customer_name, taxable: r.taxable, cgst: 0, sgst: 0, igst: r.igst, total: r.total })),
                ])}
              />
              <div className="card-flat overflow-hidden">
                <div className="p-3 border-b border-border eyebrow">B2CS (aggregate)</div>
                <table className="data-table" data-testid="b2cs-table">
                  <thead>
                    <tr><th>Place of Supply</th><th>Rate %</th><th>Inter/Intra</th><th className="num">Invoices</th><th className="num">Taxable</th><th className="num">CGST</th><th className="num">SGST</th><th className="num">IGST</th><th className="num">Total</th></tr>
                  </thead>
                  <tbody>
                    {data.gstr1.b2cs.length === 0 ? (
                      <tr><td colSpan={9} className="text-center text-muted-foreground py-6">No small B2C invoices.</td></tr>
                    ) : data.gstr1.b2cs.map((r, i) => (
                      <tr key={`b2cs-${r.state_code || 'x'}-${r.tax_rate ?? 'x'}-${i}`}>
                        <td className="font-mono">{r.place_of_supply || "—"}</td>
                        <td className="font-mono">{r.rate}%</td>
                        <td>{r.type}</td>
                        <td className="num font-mono">{r.invoice_count}</td>
                        <td className="num font-mono">{inr(r.taxable)}</td>
                        <td className="num font-mono">{inr(r.cgst)}</td>
                        <td className="num font-mono">{inr(r.sgst)}</td>
                        <td className="num font-mono">{inr(r.igst)}</td>
                        <td className="num font-mono font-semibold">{inr(r.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="card-flat overflow-hidden">
                <div className="p-3 border-b border-border eyebrow">B2CL (invoice-wise, {'>'}₹2.5L inter-state)</div>
                <table className="data-table" data-testid="b2cl-table">
                  <thead>
                    <tr><th>Invoice #</th><th>Date</th><th>Place of Supply</th><th>Customer</th><th className="num">Taxable</th><th className="num">IGST</th><th className="num">Total</th></tr>
                  </thead>
                  <tbody>
                    {data.gstr1.b2cl.length === 0 ? (
                      <tr><td colSpan={7} className="text-center text-muted-foreground py-6">No large B2C invoices for this period.</td></tr>
                    ) : data.gstr1.b2cl.map((r) => (
                      <tr key={r.invoice_id}>
                        <td className="font-mono">{r.number}</td>
                        <td className="font-mono">{fmtDate(r.date)}</td>
                        <td className="font-mono">{r.place_of_supply}</td>
                        <td>{r.customer_name}</td>
                        <td className="num font-mono">{inr(r.taxable)}</td>
                        <td className="num font-mono">{inr(r.igst)}</td>
                        <td className="num font-mono font-semibold">{inr(r.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </TabsContent>

            {/* CDNR */}
            <TabsContent value="cdnr" className="mt-4 space-y-4">
              <TabToolbar
                label="Credit / Debit Notes"
                testidPrefix="gst-cdnr"
                onExcel={() => downloadCSV(`gst-cdnr-${month}`, [
                  { key: "note_number", label: "Note #" },
                  { key: "date", label: "Date" },
                  { key: "original_invoice", label: "Original Invoice" },
                  { key: "customer_name", label: "Customer" },
                  { key: "customer_gstin", label: "GSTIN" },
                  { key: "taxable", label: "Taxable" },
                  { key: "cgst", label: "CGST" },
                  { key: "sgst", label: "SGST" },
                  { key: "igst", label: "IGST" },
                  { key: "total", label: "Total" },
                ], data.gstr1.cdnr)}
              />
              <div className="card-flat overflow-hidden">
                <table className="data-table" data-testid="cdnr-table">
                  <thead>
                    <tr><th>Note #</th><th>Date</th><th>Original Invoice</th><th>Customer</th><th>GSTIN</th><th className="num">Taxable</th><th className="num">CGST</th><th className="num">SGST</th><th className="num">IGST</th><th className="num">Total</th></tr>
                  </thead>
                  <tbody>
                    {data.gstr1.cdnr.length === 0 ? (
                      <tr><td colSpan={10} className="text-center text-muted-foreground py-6">No credit notes for this period.</td></tr>
                    ) : data.gstr1.cdnr.map((r) => (
                      <tr key={r.id}>
                        <td className="font-mono">{r.note_number}</td>
                        <td className="font-mono">{fmtDate(r.date)}</td>
                        <td className="font-mono">{r.original_invoice || "—"}</td>
                        <td>{r.customer_name}</td>
                        <td className="font-mono text-[12px]">{r.customer_gstin || "—"}</td>
                        <td className="num font-mono">{inr(r.taxable)}</td>
                        <td className="num font-mono">{inr(r.cgst)}</td>
                        <td className="num font-mono">{inr(r.sgst)}</td>
                        <td className="num font-mono">{inr(r.igst)}</td>
                        <td className="num font-mono font-semibold">{inr(r.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
}

function SectionCard({ title, count, taxable }) {
  return (
    <div className="kpi">
      <div className="eyebrow">{title}</div>
      <div className="val">{count}</div>
      <div className="text-[11px] font-mono mt-1 text-muted-foreground">{inr(taxable)}</div>
    </div>
  );
}

function Gstr3bRow({ label, row }) {
  return (
    <tr>
      <td>{label}</td>
      <td className="num font-mono">{inr(row.taxable)}</td>
      <td className="num font-mono">{inr(row.cgst)}</td>
      <td className="num font-mono">{inr(row.sgst)}</td>
      <td className="num font-mono">{inr(row.igst)}</td>
      <td className="num font-mono font-semibold">{inr(row.total)}</td>
    </tr>
  );
}
